import { Attachment } from '@dixa/lib-shared-habla';
import FileSaver from 'file-saver';
import { AsyncStatus, MessageAttachedFile } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { Component, Dispatch, ReactElement, ReactNode } from 'react';
import { connect } from 'react-redux';

import { ajaxSend } from '../../../../../api/api';
import {
  AttachmentFile,
  isPreviewableAttachment,
  prepareAttachments,
} from '../../../../../app/utils/attachments';
import { actions } from '../../../../shared/attachmentsPreviewModal/actions/attachmentsPreviewModal';

const { attachmentsPreviewModalOpen } = actions;

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    previewImage: (payload) => dispatch(attachmentsPreviewModalOpen(payload)),
  };
};

type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export type AttachedFileProps = {
  /** The url pointing to the file. */
  fileUrl: string;
  /** A human readable file. */
  prettyName?: string;
  /** All attachments for navigation */
  allAttachments?: Array<{ fileName: string; url: string }>;
  /** Extra classnames */
  className?: string;
  /** Extra buttons */
  extraButtons?: ReactElement[];
} & DispatchProps;

type AttachedFileState = {
  /** The status of the download operation. */
  downloadAsyncStatus: AsyncStatus;
  /** The status of the preview operation. */
  previewAsyncStatus: AsyncStatus;
};

export const AttachedFile = connect(
  null,
  mapDispatchToProps,
)(
  class AttachedFile extends Component<AttachedFileProps, AttachedFileState> {
    constructor(props: AttachedFileProps) {
      super(props);
      this.state = {
        downloadAsyncStatus: 'succeeded',
        previewAsyncStatus: 'succeeded',
      };
    }

    fetchPrivateFileBlob = (url: string): Promise<Blob> => {
      return ajaxSend<Blob>({
        path: url,
        responseType: 'blob',
      });
    };

    fetchPrivateFile = (url: string) => {
      this.setState({ downloadAsyncStatus: 'loading' });
      this.fetchPrivateFileBlob(url)
        .then((blob) => {
          const url = this.props.fileUrl;
          FileSaver.saveAs(
            blob,
            this.props.prettyName
              ? this.props.prettyName
              : decodeURIComponent(url.split('/').pop() as string),
          );
          this.setState({ downloadAsyncStatus: 'succeeded' });
          ampli.conversationAttachedImageIsDownloaded();
        })
        .catch((e) => {
          console.error(e);
          this.setState({ downloadAsyncStatus: 'failed' });
        });
    };

    previewPrivateFile = (fileName: string, url: string) => {
      const { previewImage, allAttachments } = this.props;
      this.setState({ previewAsyncStatus: 'loading' });
      this.fetchPrivateFileBlob(url)
        .then((blob) => window.URL.createObjectURL(blob))
        .then((blobUrl) => {
          this.setState({ previewAsyncStatus: 'succeeded' });

          // Use allAttachments if available, otherwise create single attachment array
          const attachments = allAttachments || [{ fileName, url: blobUrl }];
          const currentIndex = allAttachments
            ? allAttachments.findIndex(
                (att) => att.fileName === fileName && att.url === url,
              )
            : 0;

          previewImage({
            fileName,
            url: blobUrl,
            attachments,
            currentIndex: currentIndex >= 0 ? currentIndex : 0,
          });
          ampli.conversationAttachedImageIsViewed();
        })
        .catch((e) => {
          console.error(e);
          this.setState({ previewAsyncStatus: 'failed' });
        });
    };

    render() {
      const { fileUrl, prettyName } = this.props;

      const fileName = prettyName
        ? encodeURIComponent(prettyName)
        : decodeURIComponent(fileUrl.split('/').pop() as string);

      const onPreviewHandler = isPreviewableAttachment(fileName)
        ? () => this.previewPrivateFile(fileName, fileUrl)
        : null;

      return (
        <Attachment
          fileName={fileName}
          isDownloadLoading={this.state.downloadAsyncStatus === 'loading'}
          isPreviewLoading={this.state.previewAsyncStatus === 'loading'}
          onDownload={() => this.fetchPrivateFile(fileUrl)}
          onPreview={
            onPreviewHandler
              ? () => (onPreviewHandler ? onPreviewHandler() : null)
              : undefined
          }
          className={this.props.className}
          extraButtons={this.props.extraButtons}
        />
      );
    }
  },
);

/**
 * Creates an array of `AttachedFile` components.
 *
 * @param attachedFiles The attached files.
 */
export function createAttachedFiles(
  attachedFiles: MessageAttachedFile | MessageAttachedFile[] | null | undefined,
): ReactNode[] {
  const preparedAttachments = prepareAttachments(attachedFiles);

  // Create array of all attachments for navigation
  const allAttachments = preparedAttachments.map((file: AttachmentFile) => ({
    fileName: file.name,
    url: file.absolute_uri,
  }));

  return preparedAttachments.map((file: AttachmentFile) => (
    <AttachedFile
      prettyName={file.name}
      fileUrl={file.absolute_uri}
      allAttachments={allAttachments}
      key={file.absolute_uri}
    />
  ));
}
